import { apiClient } from './client'
import type {
  Customer,
  CustomerAgent,
  CustomerSearchResult,
  CustomerValidationStatus,
  ListSort,
  Note,
  Shipment,
  Subscription,
  SubscriptionFilter
} from './types'

export interface ListCustomersParams {
  search?: string
  from?: string
  to?: string
  enable_postnord?: boolean
  subscription_status?: SubscriptionFilter[]
  validation_status?: CustomerValidationStatus[]
  agent_id?: number[]
  sort?: ListSort
  limit?: number
  after?: number
  before?: number
}

function normalizeSearchResult(data: unknown): CustomerSearchResult {
  if (Array.isArray(data)) {
    const customers = data as Customer[]

    return { customers, total: customers.length, has_next: false, has_previous: false }
  }

  if (data && typeof data === 'object' && 'customers' in data) {
    const result = data as Partial<CustomerSearchResult>
    const customers = Array.isArray(result.customers) ? result.customers : []

    return {
      customers,
      // Sidan duger inte som total – den skulle ljuga med ett trovärdigt tal. Backend deployas först.
      total: typeof result.total === 'number' ? result.total : 0,
      has_next: result.has_next === true,
      has_previous: result.has_previous === true
    }
  }

  return { customers: [], total: 0, has_next: false, has_previous: false }
}

export async function listCustomers(params: ListCustomersParams = {}, signal?: AbortSignal): Promise<CustomerSearchResult> {
  const { data } = await apiClient.get<unknown>('/customers', { params, signal })

  return normalizeSearchResult(data)
}

export async function listCustomerAgents(): Promise<CustomerAgent[]> {
  const { data } = await apiClient.get<unknown>('/customers/agents')

  return Array.isArray(data) ? (data as CustomerAgent[]) : []
}

function normalizeCustomer(customer: Customer): Customer {
  return {
    ...customer,
    shipments: Array.isArray(customer.shipments) ? customer.shipments : [],
    delivery_plan: customer.delivery_plan ?? null
  }
}

export async function getCustomerById(id: number): Promise<Customer> {
  const { data } = await apiClient.get<unknown>(`/customers/${id}`)

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return normalizeCustomer(data as Customer)
  }

  throw new Error('Invalid customer response shape')
}

export async function getCustomerRecording(id: number): Promise<Blob> {
  const { data } = await apiClient.get(`/customers/${id}/recording`, { responseType: 'blob' })

  return data
}

export async function createCustomerNote(id: number, body: string): Promise<Note> {
  const { data } = await apiClient.post<Note>(`/customers/${id}/notes`, { body })

  return data
}

export async function terminateSubscription(id: number, finalSequence: number | null): Promise<Subscription> {
  const { data } = await apiClient.post<Subscription>(`/customers/${id}/terminate_subscription`, { final_sequence: finalSequence })

  return data
}

export async function withdrawSubscription(id: number, finalSequence: number | null): Promise<Subscription> {
  const { data } = await apiClient.post<Subscription>(`/customers/${id}/withdraw_subscription`, { final_sequence: finalSequence })

  return data
}

export async function completeReturn(id: number): Promise<Subscription> {
  const { data } = await apiClient.post<Subscription>(`/customers/${id}/complete_return`)

  return data
}

export async function endSubscription(id: number): Promise<Subscription> {
  const { data } = await apiClient.post<Subscription>(`/customers/${id}/end_subscription`)

  return data
}

export async function reactivateSubscription(id: number): Promise<Subscription> {
  const { data } = await apiClient.post<Subscription>(`/customers/${id}/reactivate_subscription`)

  return data
}

export async function pauseSubscription(id: number): Promise<Subscription> {
  const { data } = await apiClient.post<Subscription>(`/customers/${id}/pause_subscription`)

  return data
}

export async function resumeSubscription(id: number): Promise<Subscription> {
  const { data } = await apiClient.post<Subscription>(`/customers/${id}/resume_subscription`)

  return data
}

export async function createCustomerShipment(id: number, sequence: number): Promise<Shipment> {
  const { data } = await apiClient.post<Shipment>(`/customers/${id}/shipments`, { sequence })

  return data
}

export async function setSubscriptionPostnord(id: number, enablePostnord: boolean): Promise<Subscription> {
  const { data } = await apiClient.patch<Subscription>(`/customers/${id}/subscription`, { enable_postnord: enablePostnord })

  return data
}

export async function setSubscriptionCapway(id: number, enableCapway: boolean): Promise<Subscription> {
  const { data } = await apiClient.patch<Subscription>(`/customers/${id}/subscription`, { enable_capway: enableCapway })

  return data
}

export interface UpdateCustomerPayload {
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  identity_number: string
  address: string | null
  postal_code: string | null
  city: string | null
}

export async function updateCustomer(id: number, payload: UpdateCustomerPayload): Promise<Customer> {
  const { data } = await apiClient.patch<Customer>(`/customers/${id}`, payload)

  return normalizeCustomer(data)
}

export async function verifyCustomer(id: number): Promise<Customer> {
  const { data } = await apiClient.patch<Customer>(`/customers/${id}`, { validation_status: 'verified' })

  return normalizeCustomer(data)
}
